import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import { useTheme } from '@emotion/react';
import PropTypes from 'prop-types';
import {
  format,
  isSameMonth,
  isSameDay,
  isSameYear,
  subMonths,
  subWeeks,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';
import { setState } from '@chronos/ui-toolkit/actions';

import { defaultInputRanges, createStaticRanges } from 'react-date-range';
import CalendarIcon from '@chronos/assets/svg/calendar-default.svg';
import Popup from '../Popup';
import DownIcon from '../Icons/DownIcon';
import UpIcon from '../Icons/UpIcon';
import * as S from './styled';

import 'react-date-range/dist/styles.css';
import './DateRangePickerTheme.css';

const myDefaultStaticRanges = (weekStartsOn, trans) => {
  const now = new Date();
  const endOfCurrentMonth = endOfMonth(now);

  const staticDateRanges = {
    thisWeek: {
      startDate: startOfWeek(now, { weekStartsOn }),
      endDate: endOfWeek(now, { weekStartsOn }),
    },
    lastWeek: {
      startDate: subWeeks(startOfWeek(now, { weekStartsOn }), 1),
      endDate: subWeeks(endOfWeek(now, { weekStartsOn }), 1),
    },
    thisMonth: {
      startDate: startOfMonth(now),
      endDate: endOfCurrentMonth,
    },
    lastMonth: {
      startDate: startOfMonth(subMonths(now, 1)),
      endDate: endOfMonth(subMonths(now, 1)),
    },
    lastThreeMonths: {
      startDate: subMonths(startOfMonth(now), 2),
      endDate: endOfCurrentMonth,
    },
    lastSixMonths: {
      startDate: subMonths(startOfMonth(now), 5),
      endDate: endOfCurrentMonth,
    },
    thisYear: {
      startDate: startOfYear(now),
      endDate: endOfCurrentMonth,
    },
    lastYear: {
      startDate: startOfYear(subMonths(now, 12)),
      endDate: endOfYear(subMonths(now, 12)),
    },
  };
  return createStaticRanges([
    {
      label: trans('humanizeDuration.thisWeek', 'This Week'),
      range: () => staticDateRanges.thisWeek,
    },
    {
      label: trans('humanizeDuration.lastWeek', 'Last Week'),
      range: () => staticDateRanges.lastWeek,
    },
    {
      label: trans('humanizeDuration.thisMonth', 'This Month'),
      range: () => staticDateRanges.thisMonth,
    },
    {
      label: trans('humanizeDuration.lastMonth', 'Last Month'),
      range: () => staticDateRanges.lastMonth,
    },
    {
      label: trans('humanizeDuration.lastThreeMonths', 'Last Three Months'),
      range: () => staticDateRanges.lastThreeMonths,
    },
    {
      label: trans('humanizeDuration.lastSixMonths', 'Last Six Months'),
      range: () => staticDateRanges.lastSixMonths,
    },
    {
      label: trans('humanizeDuration.thisYear', 'Current Year'),
      range: () => staticDateRanges.thisYear,
    },
    {
      label: trans('humanizeDuration.lastYear', 'Last Year'),
      range: () => staticDateRanges.lastYear,
    },
  ]);
};

const initialState = {
  startDateStr: null,
  endDateStr: null,
  labelStr: '',
};

const DateRangePicker = ({
  ranges,
  weekStartsOn,
  locale,
  staticRanges,
  positions,
  maxDate,
  minDate,
  onClose,
  months,
  triggerMode,
  direction,
  align,
  showDateDisplay,
  isScrollEnabled,
  shouldTrigerLabelMatchStaticRange,
  defaultOpen,
  onChange,
}) => {
  const theme = useTheme();
  const { t: trans } = useTranslation();
  const [state, dispatch] = useReducer(keyValueReducer, initialState);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const defaultStaticRanges = useMemo(() => {
    if (staticRanges) {
      return staticRanges;
    }
    return myDefaultStaticRanges(weekStartsOn, trans);
  }, [weekStartsOn, staticRanges]);

  const datePickerRanges = useMemo(
    () =>
      ranges.map((range, i) => ({
        startDate: range.startDate,
        endDate: range.endDate,
        key: range.key || `selection${i === 0 ? '' : i}`,
      })),
    [ranges]
  );

  const handleOuterAction = useCallback(() => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const start = ranges[0].startDate;
    const end = ranges[0].endDate;

    function getLabelStr() {
      if (shouldTrigerLabelMatchStaticRange) {
        const suitedStaticRangeIndex = defaultStaticRanges.findIndex(
          (staticRange) =>
            isSameDay(staticRange.range().startDate, start) &&
            isSameDay(staticRange.range().endDate, end)
        );
        if (suitedStaticRangeIndex !== -1) {
          return defaultStaticRanges[suitedStaticRangeIndex].label;
        }
      }
      if (isSameDay(start, end)) {
        return format(start, 'MMM d');
      }
      if (isSameMonth(start, end)) {
        return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
      }
      if (!isSameYear(start, end)) {
        return `${format(start, 'MMM d, yyyy')} - ${format(
          end,
          'MMM d, yyyy'
        )}`;
      }
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }

    const labelStr = getLabelStr();

    dispatch(
      setState({
        startDateStr: format(ranges[0].startDate, 'dd MMMM yyyy'),
        endDateStr: format(ranges[0].endDate, 'dd MMMM yyyy'),
        labelStr,
      })
    );
  }, [ranges, shouldTrigerLabelMatchStaticRange]);

  const inputRanges = useMemo(
    () => [
      {
        ...defaultInputRanges[0],
        label: trans('humanizeDuration.daysUpToToday', 'days up to today'),
      },
    ],
    []
  );

  return (
    <S.DateRangePickerWrapper>
      <Popup
        isOpen={isOpen}
        handleOuterAction={handleOuterAction}
        setPopupVisible={() => {
          setIsOpen(!isOpen);
        }}
        align={align}
        positions={positions}
        trigger={
          triggerMode === 'input' ? (
            <S.DatePickerTrigger isOpen={isOpen}>
              {state.startDateStr} – {state.endDateStr}
              <S.DatePickerCalendarIcon src={CalendarIcon} />
            </S.DatePickerTrigger>
          ) : (
            <S.MonthTrigger>
              {state.labelStr}
              <S.MonthTriggerIcon>
                {isOpen ? <UpIcon size="small" /> : <DownIcon size="small" />}
              </S.MonthTriggerIcon>
            </S.MonthTrigger>
          )
        }
      >
        <S.DateRangePicker
          retainEndDateOnFirstSelection
          locale={dateFnsLocales[locale]}
          weekStartsOn={weekStartsOn}
          ranges={datePickerRanges}
          staticRanges={defaultStaticRanges}
          inputRanges={inputRanges}
          onChange={onChange}
          months={months}
          showDateDisplay={showDateDisplay}
          displayMode="dateRange"
          minDate={minDate}
          maxDate={maxDate}
          direction={direction}
          scroll={{ enabled: isScrollEnabled }}
          rangeColors={[theme.button.background, 'blue', 'green']}
        />
      </Popup>
    </S.DateRangePickerWrapper>
  );
};

DateRangePicker.propTypes = {
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.instanceOf(Date),
      endDate: PropTypes.instanceOf(Date),
      key: PropTypes.string,
    })
  ),
  staticRanges: PropTypes.arrayOf(
    PropTypes.shape({
      range: PropTypes.func,
      label: PropTypes.string,
    })
  ),
  positions: PropTypes.arrayOf(PropTypes.string),
  triggerMode: PropTypes.oneOf(['input', 'label']),
  months: PropTypes.number,
  weekStartsOn: PropTypes.number,
  locale: PropTypes.string,
  direction: PropTypes.string,
  align: PropTypes.string,
  showDateDisplay: PropTypes.bool,
  isScrollEnabled: PropTypes.bool,
  shouldTrigerLabelMatchStaticRange: PropTypes.bool,
  defaultOpen: PropTypes.bool,
};

DateRangePicker.defaultProps = {
  weekStartsOn: 0,
  locale: 'enUS',
  positions: ['bottom', 'right'],
  months: 2,
  direction: 'vertical',
  align: 'start',
  triggerMode: 'input',
  shouldTrigerLabelMatchStaticRange: false,
  showDateDisplay: false,
  isScrollEnabled: true,
  defaultOpen: false,
};

export default DateRangePicker;
